import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Palette, Globe, Clock, Bell, Save, RotateCcw, Sparkles, CheckCircle, AlertTriangle, Download, Upload, Database } from 'lucide-react';
import classNames from 'classnames';
import toast from 'react-hot-toast';
import { useSettings, DEFAULT_SETTINGS } from '../../hooks/useSettings.jsx';
import { useAuth } from '../../hooks/useAuth.jsx';
import { api, dataAPI } from '../../services/api';
import { USER_ROLES } from '../../utils/constants';
import LoadingSpinner from '../Common/LoadingSpinner';
import LanguageSwitcher from '../Common/LanguageSwitcher';

const THEME_OPTIONS = [
  { value: 'light', labelKey: 'settings.themeLight' },
  { value: 'dark', labelKey: 'settings.themeDark' },
  { value: 'system', labelKey: 'settings.themeSystem' },
];

const DATE_FORMAT_OPTIONS = [
  'MMM d, yyyy',
  'MM/dd/yyyy',
  'dd/MM/yyyy',
  'yyyy-MM-dd',
];

const WEEK_START_OPTIONS = [
  { value: 'sunday', labelKey: 'settings.weekStartSunday' },
  { value: 'monday', labelKey: 'settings.weekStartMonday' },
];

const TIMEZONE_OPTIONS = [
  { value: 'auto', label: 'Auto (browser)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'America/New_York' },
  { value: 'America/Chicago', label: 'America/Chicago' },
  { value: 'America/Denver', label: 'America/Denver' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
  { value: 'Europe/London', label: 'Europe/London' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo' },
];

const ROUND_OPTIONS = [
  { value: 'none', labelKey: 'settings.roundNone' },
  { value: '5', labelKey: 'settings.round5' },
  { value: '15', labelKey: 'settings.round15' },
];

const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={classNames(
      'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
      checked ? 'bg-primary-600' : 'bg-gray-200'
    )}
  >
    <span
      className={classNames(
        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform',
        checked ? 'translate-x-5' : 'translate-x-0'
      )}
    />
  </button>
);

const AdminAISettings = ({ t }) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(null);

  const { data: ai, isLoading } = useQuery({
    queryKey: ['ai-settings'],
    queryFn: () => api.get('/settings/ai').then((res) => res.data.ai),
  });

  useEffect(() => {
    if (ai) {
      setDraft({ enabled: ai.enabled, model: ai.model || '' });
    }
  }, [ai]);

  const saveMutation = useMutation({
    mutationFn: (data) => api.put('/settings/ai', data),
    onSuccess: () => {
      toast.success(t('admin.ai.saved'));
      queryClient.invalidateQueries({ queryKey: ['ai-settings'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || t('admin.ai.loadError'));
    },
  });

  const testMutation = useMutation({
    mutationFn: () => api.post('/settings/ai/test', {}),
    onSuccess: () => toast.success(t('admin.ai.testSuccess')),
    onError: (error) => {
      toast.error(error.response?.data?.message || t('admin.ai.testFailed'));
    },
  });

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center">
          <Sparkles className="w-5 h-5 text-primary-600 mr-2" />
          <h2 className="card-title">{t('admin.ai.title')}</h2>
        </div>
      </div>
      <div className="card-content space-y-6">
        <p className="text-sm text-gray-600">{t('admin.ai.subtitle')}</p>

        {isLoading || !draft ? (
          <div className="flex justify-center py-6">
            <LoadingSpinner size="md" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div>
                <span className="block text-sm font-medium text-gray-700">{t('admin.ai.enabled')}</span>
                <span className="block text-xs text-gray-500">{t('admin.ai.enabledHint')}</span>
              </div>
              <Toggle
                checked={draft.enabled}
                onChange={(value) => setDraft({ ...draft, enabled: value })}
                label={t('admin.ai.enabled')}
              />
            </div>

            <div>
              <label htmlFor="ai-model" className="block text-sm font-medium text-gray-700">
                {t('admin.ai.model')}
              </label>
              <input
                id="ai-model"
                type="text"
                maxLength={100}
                className="input mt-1"
                value={draft.model}
                placeholder={ai?.default_model || t('admin.ai.modelPlaceholder')}
                onChange={(e) => setDraft({ ...draft, model: e.target.value })}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('admin.ai.modelHint')}
                {ai?.default_model ? ` · ${t('admin.ai.defaultModel')}: ${ai.default_model}` : ''}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm">
              {ai?.configured ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-green-700">{t('admin.ai.configured')}</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <span className="text-amber-700">{t('admin.ai.notConfigured')}</span>
                </>
              )}
            </div>

            <p className="text-xs text-gray-500">{t('admin.ai.keyManagedInEnv')}</p>

            <div className="flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => testMutation.mutate()}
                disabled={testMutation.isPending}
                className="btn-outline btn-md flex items-center"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {testMutation.isPending ? t('admin.ai.testing') : t('admin.ai.test')}
              </button>
              <button
                type="button"
                onClick={() => saveMutation.mutate(draft)}
                disabled={saveMutation.isPending}
                className="btn-primary btn-md flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                {t('admin.ai.save')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const AdminDataTransfer = ({ t }) => {
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const CONFIRM_PHRASE = 'REPLACE ALL';

  const exportMutation = useMutation({
    mutationFn: () => dataAPI.exportData(),
    onSuccess: (res) => {
      const payload = res.data;
      const json = JSON.stringify(payload, null, 2);
      const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `timeclock-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(t('admin.data.exported'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || t('admin.data.exportFailed'));
    },
  });

  const importMutation = useMutation({
    mutationFn: async () => {
      const text = await file.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error(t('admin.data.invalidFile'));
      }
      // Accept either a raw export object or the { data: { export } } envelope.
      const payload = parsed.export || parsed.data?.export || parsed;
      return dataAPI.importData({ confirm: 'REPLACE_ALL', export: payload });
    },
    onSuccess: (res) => {
      const imported = res.data?.imported || {};
      const total = Object.values(imported).reduce((sum, n) => sum + n, 0);
      toast.success(t('admin.data.imported').replace('{count}', String(total)));
      setFile(null);
      setConfirmText('');
      // Everything may have changed: drop every cached query.
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || error.message || t('admin.data.importFailed'));
    },
  });

  const canImport = Boolean(file) && confirmText.trim().toUpperCase() === CONFIRM_PHRASE && !importMutation.isPending;

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center">
          <Database className="w-5 h-5 text-primary-600 mr-2" />
          <h2 className="card-title">{t('admin.data.title')}</h2>
        </div>
      </div>
      <div className="card-content space-y-6">
        <p className="text-sm text-gray-600">{t('admin.data.subtitle')}</p>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">{t('admin.data.warning')}</p>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="block text-sm font-medium text-gray-700">{t('admin.data.exportLabel')}</span>
            <span className="block text-xs text-gray-500">{t('admin.data.exportHint')}</span>
          </div>
          <button
            type="button"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
            className="btn-outline btn-md flex items-center justify-center"
          >
            <Download className="w-4 h-4 mr-2" />
            {exportMutation.isPending ? t('admin.data.exporting') : t('admin.data.export')}
          </button>
        </div>

        <div className="border-t border-gray-100 pt-4 space-y-3">
          <div>
            <span className="block text-sm font-medium text-gray-700">{t('admin.data.importLabel')}</span>
            <span className="block text-xs text-gray-500">{t('admin.data.importHint')}</span>
          </div>

          <input
            type="file"
            accept="application/json,.json"
            onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
            className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
          />

          <div>
            <label htmlFor="import-confirm" className="block text-xs font-medium text-gray-600">
              {t('admin.data.confirmLabel')}
            </label>
            <input
              id="import-confirm"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              className="input mt-1"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => importMutation.mutate()}
              disabled={!canImport}
              className="inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4 mr-2" />
              {importMutation.isPending ? t('admin.data.importing') : t('admin.data.import')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Settings = () => {
  const { settings, updateSettings, resetSettings, t } = useSettings();
  const { user } = useAuth();
  const [draft, setDraft] = useState(settings);

  const update = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const handleSave = () => {
    // Language is applied immediately by LanguageSwitcher, so the local
    // draft can be stale for it. Persist the live value so saving other
    // preferences does not revert the language.
    updateSettings({ ...draft, language: settings.language });
    toast.success(t('settings.saved'));
  };

  const handleReset = () => {
    resetSettings();
    setDraft({ ...DEFAULT_SETTINGS });
    toast.success(t('settings.resetDone'));
  };

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('settings.title')}</h1>
        <p className="mt-2 text-gray-600">{t('settings.subtitle')}</p>
      </div>

      <div className="space-y-6">
        {/* Appearance */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center">
              <Palette className="w-5 h-5 text-primary-600 mr-2" />
              <h2 className="card-title">{t('settings.appearance')}</h2>
            </div>
          </div>
          <div className="card-content space-y-6">
            <div>
              <span className="block text-sm font-medium text-gray-700">
                {t('settings.theme')}
              </span>
              <div
                role="radiogroup"
                aria-label={t('settings.theme')}
                className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3"
              >
                {THEME_OPTIONS.map((option) => {
                  const selected = draft.theme === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => {
                        update({ theme: option.value });
                        updateSettings({ theme: option.value });
                      }}
                      className={classNames(
                        'flex items-center justify-center rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                        selected
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      )}
                    >
                      {t(option.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <span className="block text-sm font-medium text-gray-700">
                {t('settings.language')}
              </span>
              <LanguageSwitcher variant="full" className="mt-2" />
            </div>
          </div>
        </div>

        {/* Regional */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center">
              <Globe className="w-5 h-5 text-primary-600 mr-2" />
              <h2 className="card-title">{t('settings.regional')}</h2>
            </div>
          </div>
          <div className="card-content grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label
                htmlFor="settings-date-format"
                className="block text-sm font-medium text-gray-700"
              >
                {t('settings.dateFormat')}
              </label>
              <select
                id="settings-date-format"
                className="select mt-1"
                value={draft.dateFormat}
                onChange={(e) => update({ dateFormat: e.target.value })}
              >
                {DATE_FORMAT_OPTIONS.map((format) => (
                  <option key={format} value={format}>
                    {format}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="settings-week-start"
                className="block text-sm font-medium text-gray-700"
              >
                {t('settings.weekStart')}
              </label>
              <select
                id="settings-week-start"
                className="select mt-1"
                value={draft.weekStart}
                onChange={(e) => update({ weekStart: e.target.value })}
              >
                {WEEK_START_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2">
              <label
                htmlFor="settings-timezone"
                className="block text-sm font-medium text-gray-700"
              >
                {t('settings.timezone')}
              </label>
              <select
                id="settings-timezone"
                className="select mt-1"
                value={draft.timezone}
                onChange={(e) => update({ timezone: e.target.value })}
              >
                {TIMEZONE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Time Tracking */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center">
              <Clock className="w-5 h-5 text-primary-600 mr-2" />
              <h2 className="card-title">{t('settings.timeTracking')}</h2>
            </div>
          </div>
          <div className="card-content grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label
                htmlFor="settings-hourly-rate"
                className="block text-sm font-medium text-gray-700"
              >
                {t('settings.defaultHourlyRate')}
              </label>
              <input
                id="settings-hourly-rate"
                type="number"
                min="0"
                step="0.01"
                className="input mt-1"
                value={draft.defaultHourlyRate}
                onChange={(e) =>
                  update({ defaultHourlyRate: Number(e.target.value) })
                }
              />
            </div>

            <div>
              <label
                htmlFor="settings-round-to"
                className="block text-sm font-medium text-gray-700"
              >
                {t('settings.roundTo')}
              </label>
              <select
                id="settings-round-to"
                className="select mt-1"
                value={draft.roundTo}
                onChange={(e) => update({ roundTo: e.target.value })}
              >
                {ROUND_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center">
              <Bell className="w-5 h-5 text-primary-600 mr-2" />
              <h2 className="card-title">{t('settings.notifications')}</h2>
            </div>
          </div>
          <div className="card-content divide-y divide-gray-100">
            <div className="flex items-center justify-between py-3">
              <span className="text-sm font-medium text-gray-700">
                {t('settings.emailNotifications')}
              </span>
              <Toggle
                checked={draft.emailNotifications}
                onChange={(value) => update({ emailNotifications: value })}
                label={t('settings.emailNotifications')}
              />
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm font-medium text-gray-700">
                {t('settings.weeklySummary')}
              </span>
              <Toggle
                checked={draft.weeklySummary}
                onChange={(value) => update({ weeklySummary: value })}
                label={t('settings.weeklySummary')}
              />
            </div>
          </div>
        </div>

        {/* Admin: server-side AI settings */}
        {user?.role === USER_ROLES.ADMIN && <AdminAISettings t={t} />}

        {/* Admin: whole-database export/import */}
        {user?.role === USER_ROLES.ADMIN && <AdminDataTransfer t={t} />}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={handleReset}
            className="btn-outline btn-md flex items-center justify-center"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {t('settings.reset')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary btn-md flex items-center justify-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {t('settings.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
